import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import {
  CreateShopDocument,
  CreateShopMutation,
  CreateShopMutationVariables,
  GetShopCountDocument,
  GetShopCountQuery,
  ShopListInput,
  ListShopSearchQuery,
  ListShopSearchDocument,
  ListShopSearchQueryVariables,
  ListDssConnectionSettingDocument,
  ListDssConnectionSettingQuery,
  ListDssConnectionSettingQueryVariables,
  GetIfDssShopIsDocument,
  GetIfDssShopIsQuery,
  GetBlockByZipCodeForShopRegisterDocument,
  GetBlockByZipCodeForShopRegisterQuery,
  UpdateShopMutation,
  UpdateShopMutationVariables,
  UpdateShopDocument,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { SHOP_LIST_FILE_NAME } from "@/views/admin/ShopList/constants";
import { useRouter } from "vue-router";
import type { ShopRegisterApiDeps } from "../types";

/** 店舗登録画面のAPI通信を扱う composable。 */
export const useShopRegisterApi = (deps: ShopRegisterApiDeps) => {
  const {
    shopCreateInput,
    getShopCountCountInput,
    chainSelectValues,
    dssListItems,
    dssSelectValues,
    dss_shop_name,
    dssFlg,
    overlay,
    errdialogRefSetter,
    ensureChainCode,
    zipAddressList,
    shippingLabelPrintSelectValues,
    deliveryOrderPrintSelectValues,
    tmpMemberRegistrationSelectValues,
    isDeliverableNumberDisplay,
    updateMode,
    createShopMutation,
  } = deps;

  const router = useRouter();

  /**
   * DSS接続先を取得する
   */
  const getListDssConnectionSetting = async (): Promise<void> => {
    try {
      const chainCode =
        shopCreateInput.chain_code ||
        chainSelectValues.value?.chain_code ||
        localStorageGetChainCode();
      if (!chainCode) return;
      shopCreateInput.chain_code = chainCode;

      //graphql接続
      const apolloClient = await generateApolloClient();

      const listDss: ApolloQueryResult<ListDssConnectionSettingQuery> =
        await apolloClient.query<
          ListDssConnectionSettingQuery,
          ListDssConnectionSettingQueryVariables
        >({
          query: ListDssConnectionSettingDocument,
          variables: {
            chain_code: chainCode,
            customer_code: localStorageGetCustomerCode(),
          },
        });
      const fetched = listDss.data.listDssConnectionSetting ?? [];
      if (fetched.length === 0) {
        dssListItems.value = [
          { connection_id: 0, connection_name: "連携しない" },
        ];
        Object.assign(dssSelectValues.value!, dssListItems.value[0]);
        shopCreateInput.dss_connection_id = 0;
        shopCreateInput.dss_shop_code = "";
        dss_shop_name.value = "";
        dssFlg.value = false;
        return;
      }
      dssListItems.value = [
        ...fetched,
        { connection_id: 0, connection_name: "連携しない" },
      ];

      if (dssListItems.value && dssListItems.value[0]) {
        Object.assign(dssSelectValues.value!, {
          connection_id: dssListItems.value[0]!.connection_id!,
          connection_name: dssListItems.value[0]!.connection_name!,
        });

        onChangeDssConnectionId(dssListItems.value[0]!.connection_id!, false);
      }
    } catch (err: unknown) {
      console.error(err);
      dssListItems.value = [
        { connection_id: 0, connection_name: "連携しない" },
      ];
      Object.assign(dssSelectValues.value!, dssListItems.value[0]);
      shopCreateInput.dss_connection_id = 0;
      shopCreateInput.dss_shop_code = "";
      dss_shop_name.value = "";
      dssFlg.value = false;
      throw new Error("DSS接続先の取得に失敗しました。");
    }
  };

  /**
   * 店舗コードインプット @input
   */
  const onInputShopCd = async (): Promise<void> => {
    //フォーマットチェック
    if (shopCreateInput.shop_code !== "") {
      const regex = new RegExp(/^[0-9a-zA-Z-_]+$/);
      if (!regex.test(shopCreateInput.shop_code)) {
        errdialogRefSetter(
          true,
          "店舗コードに使用できる文字は、半角英数、及びハイフンとアンダーバーです。",
        );
        return;
      }
    }
  };

  /**
   * 店舗コードフォーカスアウト @blur
   */
  const onBlurShopCd = async (): Promise<void> => {
    if (
      shopCreateInput.shop_code !== undefined &&
      shopCreateInput.shop_code !== null &&
      shopCreateInput.shop_code !== ""
    ) {
      overlay.value = true;
      try {
        //graphql接続
        const apolloClient = await generateApolloClient();

        //条件を設定
        getShopCountCountInput.customer_code = localStorageGetCustomerCode();
        getShopCountCountInput.chain_code = shopCreateInput.chain_code;
        getShopCountCountInput.shop_list = [shopCreateInput.shop_code];

        // 店舗コードに存在しているかチェック
        const countShopCd: ApolloQueryResult<GetShopCountQuery> =
          await apolloClient.query<GetShopCountQuery>({
            query: GetShopCountDocument,
            variables: {
              input: getShopCountCountInput,
            },
          });

        if ((countShopCd.data.getShopCount!.count ?? 0) > 0) {
          errdialogRefSetter(
            true,
            `店舗コード ${shopCreateInput.shop_code} は既に存在しています。`,
          );
          shopCreateInput.shop_code = "";
          return;
        }
        shopCreateInput.dss_shop_code = shopCreateInput.shop_code;
        onBlurDssShopCd();
      } catch (err: unknown) {
        errdialogRefSetter(
          true,
          err instanceof Error ? err.message : "エラーが発生しました。",
        );
      } finally {
        overlay.value = false;
      }
    }
  };

  /**
   * 店舗名フォーカスアウト @blur
   */
  const onBlurShopName = async (): Promise<void> => {
    //店舗名が入力されていたら店舗名重複チェック
    if (
      shopCreateInput.name !== undefined &&
      shopCreateInput.name !== null &&
      shopCreateInput.name !== ""
    ) {
      checkShopName();
    }
  };

  const checkShopName = async (): Promise<void> => {
    shopCreateInput.name = shopCreateInput.name.trim();
    overlay.value = true;
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 一覧検索条件
      const listInput: ShopListInput = {
        chain_code:
          shopCreateInput.chain_code !== undefined &&
          shopCreateInput.chain_code !== null &&
          shopCreateInput.chain_code !== ""
            ? shopCreateInput.chain_code
            : localStorageGetChainCode(),
        name:
          shopCreateInput.name !== undefined &&
          shopCreateInput.name !== null &&
          shopCreateInput.name !== ""
            ? shopCreateInput.name
            : "",
        customer_code: localStorageGetCustomerCode(),
        limit: 1,
        offset: 0,
      };

      //件数取得用
      const CountInput = {
        chain_code:
          shopCreateInput.chain_code !== undefined &&
          shopCreateInput.chain_code !== null &&
          shopCreateInput.chain_code !== ""
            ? shopCreateInput.chain_code
            : localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
      };

      // 店舗一覧取得
      let result: ApolloQueryResult<ListShopSearchQuery>;
      try {
        result = await apolloClient.query<
          ListShopSearchQuery,
          ListShopSearchQueryVariables
        >({
          query: ListShopSearchDocument,
          variables: {
            listInput: listInput,
            countInput: CountInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        errdialogRefSetter(
          true,
          err instanceof Error
            ? "店舗名の重複確認に失敗しました。"
            : "不明なエラーが発生しました。",
        );
        return;
      }

      if (
        result.data.listShop === undefined ||
        result.data.listShop === null ||
        result.data.getShopCount === undefined ||
        result.data.getShopCount === null
      ) {
        if (result.data.listShop === undefined || result.data.listShop === null) {
          // 店舗一覧取得 エラー
          console.error("result.data.listShop is undefined or null");
          errdialogRefSetter(true, "店舗名の重複確認に失敗しました。");
          return;
        }
        if (
          result.data.getShopCount === undefined ||
          result.data.getShopCount === null
        ) {
          // 店舗件数 エラー
          console.error("result.data.getShopCount is undefined or null");
          errdialogRefSetter(true, "店舗名の重複確認に失敗しました。");
          return;
        }
      }

      // 店舗一覧のデータを作成
      const list = result.data.listShop;
      list.forEach((value) => {
        if (value !== undefined && value !== null) {
          if (
            value.name == shopCreateInput.name &&
            value.shop_code != shopCreateInput.shop_code
          ) {
            errdialogRefSetter(
              true,
              `店舗名 ${shopCreateInput.name} は既に存在しています。`,
            );
          }
        }
      });
    } catch (err: unknown) {
      errdialogRefSetter(
        true,
        err instanceof Error ? err.message : "不明なエラーが発生しました。",
      );
    } finally {
      overlay.value = false;
    }
  };

  /**
   * DSSショップコードチェック @blur
   */
  const onBlurDssShopCd = async (): Promise<void> => {
    if (
      shopCreateInput.dss_shop_code != "" &&
      dssSelectValues.value!.connection_id != 0
    ) {
      shopCreateInput.dss_shop_code = shopCreateInput!.dss_shop_code!.trim();

      try {
        //graphql接続
        const apolloClient = await generateApolloClient();
        const dssShopCd: ApolloQueryResult<GetIfDssShopIsQuery> =
          await apolloClient.query<GetIfDssShopIsQuery>({
            query: GetIfDssShopIsDocument,
            variables: {
              dss_connection_id: dssSelectValues.value!.connection_id,
              dss_shop_code: shopCreateInput.dss_shop_code,
            },
          });

        if (dssShopCd.data.getIfDssShopIs!["result"] == 1) {
          errdialogRefSetter(
            true,
            "指定のDSS店舗コードと接続IDの設定は存在しません",
          );
          shopCreateInput.dss_shop_code = "";
          dss_shop_name.value = "";
          return;
        }
        dss_shop_name.value =
          dssShopCd.data.getIfDssShopIs!["shop_data"]!["shop_name"]!;
      } catch (err: unknown) {
        errdialogRefSetter(
          true,
          "DSSサーバーへの接続に失敗しました",
        );
      } finally {
        overlay.value = false;
      }
    }
  };

  /**
   * DSS接続先変更 @change
   */
  const onChangeDssConnectionId = async (
    connection_id: number,
    connection_check: boolean,
  ): Promise<void> => {
    if (connection_id !== 0) {
      dssFlg.value = true;
      if (connection_check) {
        onBlurDssShopCd();
      }
    } else {
      dssFlg.value = false;
    }
  };

  /**
   * 店舗作成
   */
  const createShop = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    if (zipAddressList.value === undefined || zipAddressList.value === null) {
      //住所から区画コードを取得する
      const adddressBlock: ApolloQueryResult<GetBlockByZipCodeForShopRegisterQuery> =
        await apolloClient.query<GetBlockByZipCodeForShopRegisterQuery>({
          query: GetBlockByZipCodeForShopRegisterDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainSelectValues.value!.chain_code,
            zip_code: shopCreateInput.zip_code,
          },
        });

      zipAddressList.value = adddressBlock.data.getBlockByZipCodeForShopRegister;
    }

    if (zipAddressList.value !== undefined && zipAddressList.value !== null) {
      for (const zipAddress of zipAddressList.value) {
        let checkAddress = "";

        if (zipAddress !== null && zipAddress !== undefined) {
          if (
            zipAddress["pref_name"] !== null &&
            zipAddress["pref_name"] !== undefined
          ) {
            checkAddress += zipAddress["pref_name"];
          }
          if (
            zipAddress["city_name"] !== null &&
            zipAddress["city_name"] !== undefined
          ) {
            checkAddress += zipAddress["city_name"];
          }
          if (
            zipAddress["section_name"] !== null &&
            zipAddress["section_name"] !== undefined
          ) {
            checkAddress += zipAddress["section_name"];
          }
          if (
            zipAddress["block_name"] !== null &&
            zipAddress["block_name"] !== undefined
          ) {
            checkAddress += zipAddress["block_name"];
          }

          if (shopCreateInput.address1 == checkAddress.replace(/\s+/g, "")) {
            shopCreateInput.lot_code = zipAddress["lot_code"]!;
          }
        }
      }
    }

    //念のためトリムしておく
    shopCreateInput.address2 = shopCreateInput.address2.trim();
    shopCreateInput.address3 = (shopCreateInput.address3 ?? "").trim();

    //荷札印刷
    shopCreateInput.shipping_label_print_flg =
      shippingLabelPrintSelectValues.value.index;

    //配達伝票印刷
    shopCreateInput.delivery_order_print_flg =
      deliveryOrderPrintSelectValues.value.index;

    //仮会員登録機能
    shopCreateInput.tmp_member_registration_flg =
      tmpMemberRegistrationSelectValues.value.index;

    // 配送依頼受付通知先メールアドレス
    if (!deliveryOrderPrintSelectValues.value.index) {
      // 配達伝票印刷 が 印刷しない の場合：
      // nullにする
      // 型が nullable でない場合は GraphQL 側の型定義に合わせて適宜修正
      (
        shopCreateInput as unknown as {
          delivery_request_notification_emails: string | null;
        }
      ).delivery_request_notification_emails = null;
    }

    // 2件分配達
    if (
      shopCreateInput.double_deli_oricon_count == 0 ||
      shopCreateInput.double_deli_oricon_count == null
    ) {
      // 0 または 空文字が入力された場合：
      // nullにする
      shopCreateInput.double_deli_oricon_count = null;
    }

    // チャーター便個口
    if (
      shopCreateInput.charter_deli_oricon_count == 0 ||
      shopCreateInput.charter_deli_oricon_count == null
    ) {
      // 0 または 空文字が入力された場合：
      // nullにする
      shopCreateInput.charter_deli_oricon_count = null;
    }

    if (updateMode.value === true) {
      await apolloClient.mutate<UpdateShopMutation, UpdateShopMutationVariables>({
        mutation: UpdateShopDocument,
        variables: {
          input: shopCreateInput,
        },
      });

      await router.push({
        name: SHOP_LIST_FILE_NAME,
        query: { searchMode: "true" },
      });
    } else {
      const result: FetchResult<CreateShopMutation> = await apolloClient.mutate<
        CreateShopMutation,
        CreateShopMutationVariables
      >({
        mutation: CreateShopDocument,
        variables: {
          input: shopCreateInput,
        },
      });
      createShopMutation.value = result.data;
    }
  };

  return {
    getListDssConnectionSetting,
    onInputShopCd,
    onBlurShopCd,
    onBlurShopName,
    checkShopName,
    onBlurDssShopCd,
    onChangeDssConnectionId,
    createShop,
  };
};
